/**
 * How every number in this app is printed.
 *
 * Two rules from the verifier's own documentation are enforced here rather than at each call
 * site, because getting either one wrong would make the app lie about the book:
 *
 * - A predicate that judged nothing scores `null`, never `1.0`. Silence is not approval, so it
 *   prints as an em dash and is never drawn as a full bar.
 * - A rate short of the whole stops one step below it, and a rate above nothing stops one step
 *   above. `compass` scores 0.99967 over the corpus — 34 exceptions — and would round to
 *   `100.0%`, which reads as a predicate nothing failed.
 *
 * This is `rk_check.clean_rate` in TypeScript, clamp for clamp: the app must print the number
 * the CLI prints, so 84.2% overall and 99.9% for compass, never a flooring of its own.
 */

/** A rate in [0, 1] as a percentage string, clamped so a fault is never rounded away. */
export function rate(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return "—";
  return `${percent(value, digits).toFixed(digits)}%`;
}

/** The number behind {@link rate}, for bar widths and comparisons. */
export function percent(value: number | null | undefined, digits = 1): number {
  if (value === null || value === undefined) return 0;
  const step = 1 / 10 ** (digits + 2);
  let clamped = value;
  if (clamped < 1) clamped = Math.min(clamped, 1 - step);
  if (clamped > 0) clamped = Math.max(clamped, step);
  return clamped * 100;
}

/** A plain count, or an em dash when there is nothing to count. */
export function count(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString("en-GB");
}

/** A number of semitones, signed, for drift and gaps. */
export function semitones(value: number | null | undefined, digits = 2): string {
  return value === null || value === undefined ? "—" : value.toFixed(digits);
}

/** Key signature in fifths, as the reader would name it. */
export function keyName(fifths: number | null | undefined): string {
  if (fifths === null || fifths === undefined) return "no key";
  if (fifths === 0) return "no sharps or flats";
  const many = Math.abs(fifths);
  const kind = fifths > 0 ? "sharp" : "flat";
  return `${many} ${kind}${many === 1 ? "" : "s"}`;
}

/** Turn an instrument or predicate id into the words a reader expects. */
export function label(value: string): string {
  return value.replace(/_/g, " ");
}

/** A byte count, for the export panel and the size of a score. */
export function bytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
